import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Heart, ShieldCheck, Info, ArrowRight, Copy, CheckCircle2, Repeat, Banknote, Globe, HandHeart, Quote } from "lucide-react";
import { DonationUse } from "../components/TrustBlocks";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ErrorSummary } from "../components/ErrorSummary";
import { usePageMeta, useJsonLd, ORG_LD } from "../lib/seo";

const NGN_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000];
const USD_AMOUNTS = [25, 50, 100, 250];
const GBP_AMOUNTS = [25, 50, 100];

const NIGERIA_METHODS = [
  { key: "paystack", label: "Card (Paystack)", testid: "method-paystack" },
  { key: "bank", label: "Bank transfer (₦)" },
];
const INTL_METHODS = [
  { key: "stripe", label: "Card (Stripe)", testid: "method-card" },
];
const FUNDS = [
  { key: "general", label: "General Fund", d: "Used where the need is verified and greatest.", programme: "Where most needed" },
  { key: "project", label: "A Verified Project", d: "Restricted to one project that has passed verification.", programme: "" },
  { key: "emergency", label: "Emergency Fund", d: "Rapid humanitarian response after triage.", programme: "Emergency Relief Fund" },
  { key: "education", label: "Education Fund", d: "School fees, materials and sponsorship.", programme: "Education Support" },
  { key: "monthly", label: "Monthly Impact Partner", d: "A regular gift that lets us plan ahead.", programme: "Where most needed", type: "monthly" },
];

const PURPOSES = [
  ["small_business", "Start a small business"],
  ["expand_business", "Expand an existing business"],
  ["farming", "Farming / Agriculture"],
  ["skills", "Skills / Tools / Equipment"],
  ["education", "Education / vocational training"],
  ["women", "Women's empowerment"],
  ["youth", "Youth empowerment"],
  ["community", "Community livelihood activity"],
  ["other", "Other"],
];

const EMPTY_BEN = {
  full_name: "", date_of_birth: "", gender: "", phone: "", email: "",
  residential_address: "", state: "", lga: "", ward: "",
  occupation: "", employment_status: "unemployed", household_size: 1,
  id_type: "NIN", id_number: "", photo_url: "",
  purpose: "small_business", purpose_details: "",
};

const IMPACT = {
  NGN: { 1000: "School materials for a child", 2000: "Hot meals for a family for two days", 5000: "Emergency food for a family for a week", 10000: "Clean water for a household for a month", 20000: "Keeps a child in school for a term", 50000: "Starter kit for a trained tailor", 100000: "Solar light for a classroom", 500000: "Repairs one unsafe family home" },
  USD: { 25: "Emergency food for a family for a week", 50: "Keeps a child in school for a term", 100: "Solar light for a classroom", 250: "Seed capital for one small business" },
  GBP: { 25: "Emergency food for a family for a week", 50: "Keeps a child in school for a term", 100: "Solar light for a classroom" },
};

export default function Donate() {
  const nav = useNavigate();
  usePageMeta("Donate — Support communities in Adamawa State", "Give in Naira, USD or GBP. 85% of every gift funds programmes directly. Stripe or GTBank transfer; instant receipt and a verifiable DKF donor serial.");
  useJsonLd("donate", { "@type": "DonateAction", name: "Donate to Dutsen Kauna Foundation", recipient: ORG_LD, target: { "@type": "EntryPoint", urlTemplate: window.location.origin + "/donate", actionPlatform: ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"] }, priceCurrency: "NGN" });
  useJsonLd("faq", { "@type": "FAQPage", mainEntity: [
    { "@type": "Question", name: "How is my donation used?", acceptedAnswer: { "@type": "Answer", text: "85% funds programmes directly, 10% covers operations and 5% reserves for emergencies. Every gift receives a DKF donor serial you can verify." } },
    { "@type": "Question", name: "Can I donate in Naira by bank transfer?", acceptedAnswer: { "@type": "Answer", text: "Yes. Transfer to GTBank account 07064868547 (Dutsen Kauna Foundation) and use your donor reference as the description. International card gifts (£/US$/€) go through Stripe." } },
    { "@type": "Question", name: "Can I give monthly?", acceptedAnswer: { "@type": "Answer", text: "Yes — choose Monthly on the donation form. Recurring gifts let us plan school terms, water projects and emergency relief in Adamawa State." } },
    { "@type": "Question", name: "Does donating affect anyone's eligibility for support?", acceptedAnswer: { "@type": "Answer", text: "No. Donations never influence who receives help; all applications are free and assessed on need." } },
  ] });
  const [dash, setDash] = useState(null);
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/org/contact").then((r) => setOrg(r.data)).catch(() => {}); }, []);
  const [showPolicy, setShowPolicy] = useState(false);
  useEffect(() => { api.get("/impact-dashboard").then((r) => setDash(r.data)).catch(() => {}); }, []);
  const [sp] = useSearchParams();
  const [currency, setCurrency] = useState(["NGN", "USD", "GBP"].includes(sp.get("currency")) ? sp.get("currency") : "NGN");
  const [amount, setAmount] = useState(sp.get("amount") || "");
  const [cardNgn, setCardNgn] = useState(null);
  useEffect(() => { api.get("/payments/paystack/config").then((r) => setCardNgn(!!r.data.card_enabled)).catch(() => setCardNgn(false)); }, []);
  const [method, setMethod] = useState("paystack");
  useEffect(() => { if (cardNgn === false) setMethod((m) => (m === "paystack" ? "bank" : m)); }, [cardNgn]);
  const [transfer, setTransfer] = useState({ confirmed: false, sender_bank: "", transfer_date: "" });
  const [projectSerial] = useState(sp.get("project") || "");
  const [projectStage, setProjectStage] = useState(null);
  useEffect(() => { if (projectSerial) api.get(`/projects/${encodeURIComponent(projectSerial)}`).then((r) => setProjectStage(r.data)).catch(() => setProjectStage(null)); }, [projectSerial]);
  const [programmeName] = useState(sp.get("programme") || "");
  const [donationType, setDonationType] = useState(sp.get("type") || "monthly");
  const [fund, setFund] = useState(sp.get("project") ? "project" : sp.get("type") === "monthly" ? "monthly" : sp.get("programme")?.toLowerCase().includes("emergency") ? "emergency" : sp.get("programme")?.toLowerCase().includes("education") ? "education" : sp.get("fund") || "");
  const [fundProjects, setFundProjects] = useState([]);
  const [errors, setErrors] = useState([]);
  const [fundProject, setFundProject] = useState(sp.get("project") || "");
  useEffect(() => { api.get("/projects").then((r) => setFundProjects(r.data.filter((p) => p.stage === "verified_fundraising"))).catch(() => {}); }, []);
  const chooseFund = (f) => { setFund(f.key); if (f.type === "monthly") setDonationType("monthly"); else if (fund === "monthly") setDonationType("one_time"); };
  const fundMeta = FUNDS.find((f) => f.key === fund);
  const chosenProject = fundProjects.find((p) => p.serial === fundProject);
  const effectiveProgramme = fund === "project" ? (chosenProject?.title || programmeName) : (fundMeta?.programme || programmeName || "Where most needed");
  const effectiveProjectSerial = fund === "project" ? fundProject : "";
  const [donor, setDonor] = useState({ full_name: "", email: "", phone: "" });
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
  }, []);


  useEffect(() => {
    document.title = "Donate | Dutsen Kauna Foundation";
    const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.name = "description";
    meta.content = "Donate to support clean energy, education, women and youth empowerment, food, water and emergency relief projects in underserved Nigerian communities.";
    if (!meta.parentNode) document.head.appendChild(meta);
  }, []);

  const prevCurrency = useRef(currency);
  useEffect(() => {
    if (prevCurrency.current === currency) return;
    prevCurrency.current = currency;
    setAmount("");
    if (currency === "NGN") setMethod(cardNgn === false ? "bank" : "paystack");
    else setMethod("stripe");
  }, [currency, cardNgn]);

  const presets = currency === "NGN" ? NGN_AMOUNTS : currency === "USD" ? USD_AMOUNTS : GBP_AMOUNTS;
  const symbol = currency === "NGN" ? "₦" : currency === "USD" ? "$" : "£";
  const methods = currency === "NGN" ? NIGERIA_METHODS.filter((m) => m.key !== "paystack" || cardNgn !== false) : INTL_METHODS;
  const bankStep = currency === "NGN" && method === "bank";
  const bankReady = !bankStep || (transfer.confirmed && transfer.sender_bank.trim() && transfer.transfer_date);

  const submit = async (e) => {
    e.preventDefault();
    const errs = [];
    if (!fund) errs.push({ field: "fund-general", message: "Choose where you would like to give" });
    if (fund === "project" && !fundProject) errs.push({ field: "fund-project-select", message: "Choose a verified project, or give to the General Fund" });
    if (!amount || Number(amount) <= 0) errs.push({ field: "custom-amount", message: "Choose or enter an amount greater than zero" });
    if (!donor.full_name) errs.push({ field: "donor-name", message: "Enter your full name" });
    if (!donor.email) errs.push({ field: "donor-email", message: "Enter your email so we can send your receipt" });
    if (bankStep && !transfer.confirmed) errs.push({ field: "transfer-confirmed", message: "Tick to confirm you have made the bank transfer" });
    if (bankStep && !transfer.sender_bank.trim()) errs.push({ field: "transfer-bank", message: "Enter the bank you transferred from" });
    if (bankStep && !transfer.transfer_date) errs.push({ field: "transfer-date", message: "Enter the date of your transfer" });
    setErrors(errs);
    if (errs.length) { toast.error(errs[0].message); return; }

    setLoading(true);
    try {
      // Stripe checkout for international currencies (USD/GBP/EUR)
      if (method === "stripe" && ["USD", "GBP", "EUR"].includes(currency)) {
        const { data } = await api.post("/payments/stripe/create-session", {
          amount: Number(amount),
          currency: currency.toLowerCase(),
          origin_url: window.location.origin,
          donor_name: donor.full_name || (anonymous ? "Anonymous" : "Donor"),
          donor_email: donor.email,
          donor_phone: donor.phone || "",
          programme: effectiveProgramme,
          donation_type: donationType,
          project_serial: effectiveProjectSerial,
        });
        // redirect to Stripe Checkout
        window.location.href = data.checkout_url;
        return;
      }
      if (method === "paystack" && currency === "NGN") {
        const { data } = await api.post("/payments/paystack/initialize", {
          amount: Number(amount), origin_url: window.location.origin,
          donor_name: donor.full_name || (anonymous ? "Anonymous" : "Donor"), donor_email: donor.email, donor_phone: donor.phone || "",
          programme: effectiveProgramme, donation_type: donationType, project_serial: effectiveProjectSerial, anonymous,
        });
        window.location.href = data.authorization_url;
        return;
      }
      const payload = {
        full_name: donor.full_name || (anonymous ? "Anonymous" : ""),
        email: donor.email,
        phone: donor.phone,
        amount: Number(amount),
        currency,
        donation_type: donationType,
        method,
        anonymous,
        also_register_beneficiary: false,
        beneficiary: null,
        project_serial: effectiveProjectSerial,
        programme: effectiveProgramme,
        transfer_confirmed: bankStep ? transfer.confirmed : false,
        sender_bank: bankStep ? transfer.sender_bank.trim() : "",
        transfer_date: bankStep ? transfer.transfer_date : "",
      };
      const { data } = await api.post("/donations", payload);
      nav(`/thank-you/${data.donor_serial}?t=${data.thank_you_token || ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="donate-success" className="bg-[#f8f4e8] py-16 min-h-[70vh]">
        <div className="dk-container max-w-2xl">
          <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20">
            <CheckCircle2 className="text-[#d6aa43] mx-auto mb-4" size={48} />
            <h1 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mb-3">Thank you.</h1>
            <p className="text-[#0a0d12]/75 mb-6">Your generosity funds real change. Please keep the reference(s) below safely.</p>

            <div className="p-6 rounded-xl bg-[#155e3f] text-white mb-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">Donor reference</div>
              <div className="font-mono text-2xl md:text-3xl font-bold text-[#d6aa43] mb-2" data-testid="donor-serial">{result.donor_serial}</div>
              <button onClick={() => navigator.clipboard.writeText(result.donor_serial).then(() => toast.success("Copied"))} className="text-xs text-white inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
            </div>

            {result.beneficiary_serial && (
              <div className="p-6 rounded-xl bg-[#d6aa43]/10 border-2 border-[#d6aa43]/40 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-[#0d1724] mb-2 font-semibold">Beneficiary reference (separate)</div>
                <div className="font-mono text-2xl md:text-3xl font-bold text-[#0d1724] mb-2" data-testid="ben-serial-from-donate">{result.beneficiary_serial}</div>
                <button onClick={() => navigator.clipboard.writeText(result.beneficiary_serial).then(() => toast.success("Copied"))} className="text-xs text-[#0d1724]/70 inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
                <p className="text-xs text-[#0d1724]/70 mt-3 max-w-md mx-auto">Only your BEN serial enters beneficiary selection. Your donor reference is kept separate.</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/empowerment" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">Programme details</Link>
              <Link to="/" className="dk-btn-primary">Return home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="donate-page" className="bg-[#f8f4e8]">
      <section className="relative bg-[#155e3f] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Give and help someone rise</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-3xl">Support the Work of Dutsen Kauna Foundation</h1>
          {projectSerial ? (
            <div className="mt-5 inline-flex items-start gap-3 bg-[#d6aa43]/10 border border-[#d6aa43]/30 rounded-lg px-4 py-3 text-sm" data-testid="donate-project-banner">
              <HandHeart className="text-[#d6aa43] shrink-0 mt-0.5" size={18} />
              <div>
                <div className="text-white/90 text-[10px] uppercase tracking-[0.2em] font-bold">Sponsoring project</div>
                <div className="font-serif text-white text-lg font-bold">{programmeName || projectSerial}</div>
                <div className="font-mono text-[#d6aa43] text-xs">{projectSerial}</div>
                {projectStage?.stage === "under_verification" && (
                  <div className="mt-3 text-[#f0ce77] text-xs leading-relaxed max-w-md" data-testid="donate-under-verification">
                    This project is still <strong>under verification</strong>, so project-specific donations are not yet open. You can <Link to="/projects?stage=under_verification" className="underline">register interest</Link> to be told when funding opens, or give to the General Fund below — your gift will be used where the need is verified and greatest.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-5 text-white text-lg max-w-2xl">Your contribution supports people, families and communities through the Foundation's programmes.</p>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/empowerment/register" className="dk-btn-outline" data-testid="donate-hero-register-free">
              Register free without donating <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="dk-container grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 -mb-2 p-4 rounded-xl bg-[#f8f4e8] border border-[#d6aa43]/30 text-sm text-[#0a0d12]/85 flex gap-3 items-start" data-testid="donate-impact-line"><Quote size={16} className="text-[#d6aa43] flex-shrink-0 mt-0.5" /><span><strong>1,150 direct-support entries recorded in 2023 (Consolidated Impact Report 2021–2025).</strong> "The solar light means my children can read at night." — a mother in Yola North, Adamawa. Every gift is tracked to a DKF serial you can verify.</span></div>
          <form onSubmit={submit} noValidate className="lg:col-span-3 bg-white rounded-2xl p-8 border border-black/5 shadow-sm">

            <ErrorSummary errors={errors} testid="donate-error-summary" />
            {/* Fund selector */}
            <div className="mb-6" data-testid="fund-selector">
              <div className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2">1. Where would you like to give?</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {FUNDS.map((f) => (
                  <button key={f.key} type="button" onClick={() => chooseFund(f)} className={`p-3 rounded-md text-left border-2 transition ${fund === f.key ? "bg-[#155e3f] border-[#0d1724] text-white" : "border-black/10 hover:border-[#d6aa43]"}`} data-testid={`fund-${f.key}`}>
                    <div className="text-sm font-bold">{f.label}</div><div className={`text-xs mt-0.5 ${fund === f.key ? "text-white" : "text-[#0a0d12]/60"}`}>{f.d}</div>
                  </button>
                ))}
              </div>
              {fund === "project" && (
                <div className="mt-3" data-testid="fund-project-picker">
                  {fundProjects.length ? (
                    <select value={fundProject} onChange={(e) => setFundProject(e.target.value)} className="w-full px-4 py-3 border border-black/15 rounded-md bg-white text-sm" data-testid="fund-project-select">
                      <option value="">Choose a verified project…</option>
                      {fundProjects.map((p) => <option key={p.serial} value={p.serial}>{p.title} — {p.serial}{p.target_amount_numeric ? ` (₦${Number(p.target_amount_numeric).toLocaleString()})` : ""}</option>)}
                    </select>
                  ) : <p className="text-xs text-[#8a6a1f] bg-[#f8f4e8] border border-[#d6aa43]/30 rounded-md p-3" data-testid="fund-project-empty">No project is open for project-specific funding right now — every current project is still under verification. Choose the General Fund, or <Link to="/projects?stage=under_verification" className="underline">register interest</Link> in a project to be told when it opens.</p>}
                </div>
              )}
            </div>

            {/* Currency */}
            <div className={`mb-5 ${fund ? "" : "opacity-50 pointer-events-none"}`}>
              <div className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2">2. Where are you giving from? (one currency per gift)</div>
              <div className="flex gap-2" data-testid="currency-tabs">
                {[["NGN", Banknote, "Nigeria · ₦"], ["GBP", Globe, "United Kingdom · £"], ["USD", Globe, "International · US$"]].map(([c, Icon, lbl]) => (
                  <button key={c} type="button" onClick={() => setCurrency(c)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border-2 transition ${currency === c ? "bg-[#155e3f] border-[#0d1724] text-[#d6aa43]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                    data-testid={`currency-${c.toLowerCase()}`}>
                    <Icon size={14} /> {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2">Amount</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {presets.map((a) => (
                  <button key={a} type="button" onClick={() => setAmount(String(a))}
                    className={`py-3 rounded-md text-sm font-bold border-2 transition ${String(amount) === String(a) ? "bg-[#d6aa43] border-[#d6aa43] text-[#0a0d12]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                    data-testid={`amount-${currency.toLowerCase()}-${a}`}>
                    <span className="block">{symbol}{a.toLocaleString()}</span>
                    {IMPACT[currency]?.[a] && <span className="block text-[10px] font-medium leading-tight mt-1 opacity-80" data-testid={`impact-${currency.toLowerCase()}-${a}`}>{IMPACT[currency][a]}</span>}
                  </button>
                ))}
              </div>
              <div className="mt-3 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0d12]/60 font-bold">{symbol}</span>
                <input id="custom-amount" aria-label="Custom amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Custom amount"
                  className="w-full pl-8 pr-4 py-3 border border-black/10 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid="custom-amount" />
              </div>
            </div>

            {/* Donation type */}
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2">Donation type</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="type-tabs">
                {[["one-time", "One-time"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["annual", "Annual"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setDonationType(k)}
                    className={`flex items-center justify-center gap-2 py-3 rounded-md text-sm font-semibold border-2 transition ${donationType === k ? "bg-[#155e3f] border-[#0d1724] text-[#d6aa43]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                    data-testid={`type-${k}`}>
                    {k !== "one-time" && <Repeat size={12} />} {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Method */}
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2">Payment method</div>
              <div className={`grid gap-2 ${methods.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                {methods.map((m) => (
                  <button key={m.key} type="button" onClick={() => setMethod(m.key)}
                    className={`px-3 py-3 rounded-md text-sm font-semibold border-2 transition ${method === m.key ? "bg-[#155e3f] border-[#0d1724] text-[#d6aa43]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                    data-testid={m.testid || `method-${m.key}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Donor info */}
            <div className="mb-5 grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="donor-name" className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Your name</label>
                <input id="donor-name" value={donor.full_name} onChange={(e) => setDonor({ ...donor, full_name: e.target.value })} className="w-full px-4 py-3 border border-black/10 rounded-md" data-testid="donor-name" />
              </div>
              <div>
                <label htmlFor="donor-email" className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Email *</label>
                <input id="donor-email" required aria-required="true" type="email" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })} className="w-full px-4 py-3 border border-black/10 rounded-md" data-testid="donor-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Phone</label>
                <input value={donor.phone} onChange={(e) => setDonor({ ...donor, phone: e.target.value })} className="w-full px-4 py-3 border border-black/10 rounded-md" data-testid="donor-phone" />
              </div>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="anonymous-check" /> Donate anonymously
              </label>
            </div>

            <div className="mb-5 text-xs text-[#0a0d12]/60 flex items-start gap-2" data-testid="donate-get-help-note"><Info size={14} className="text-[#d6aa43] mt-0.5 flex-shrink-0" /> <span>Need support yourself? Giving never affects eligibility — <Link to="/get-help" className="underline text-[#0d1724]">apply for help here</Link>.</span></div>

            {method === "bank" && org && (
              <div className="p-4 rounded-md bg-[#155e3f] text-white mb-5 text-sm" data-testid="bank-details">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#d6aa43] font-bold mb-2">Naira bank transfer</div>
                <div className="grid sm:grid-cols-3 gap-3"><div><div className="text-[10px] text-white uppercase">Bank</div><b>{org.bank_name}</b></div><div><div className="text-[10px] text-white uppercase">Account number</div><b className="font-mono text-lg select-all" data-testid="donate-bank-account">{org.account_number}</b></div><div><div className="text-[10px] text-white uppercase">Account name</div><b>{org.account_name}</b></div></div>
                <p className="text-xs text-white mt-3">{org.bank_note}</p>
              </div>
            )}
            {bankStep && (
              <div className="p-4 rounded-md bg-[#f8f4e8] border-2 border-[#d6aa43]/40 mb-5 text-sm" data-testid="transfer-confirmation">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#7a5a0f] font-bold mb-2">Step 2 — confirm your transfer</div>
                <p className="text-xs text-[#0a0d12]/70 mb-3">Make the transfer first using your name as the description, then tell us where it came from so Finance can match it and send your receipt.</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <label className="block"><span className="text-[10px] uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold block mb-1">Bank you transferred from</span>
                    <input id="transfer-bank" value={transfer.sender_bank} onChange={(e) => setTransfer({ ...transfer, sender_bank: e.target.value })} placeholder="e.g. Access Bank, Opay, Kuda" maxLength={80} className="w-full px-3 py-2 border border-black/15 rounded-md bg-white" data-testid="transfer-bank" aria-invalid={errors.some((x) => x.field === "transfer-bank")} /></label>
                  <label className="block"><span className="text-[10px] uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold block mb-1">Date of transfer</span>
                    <input id="transfer-date" type="date" value={transfer.transfer_date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setTransfer({ ...transfer, transfer_date: e.target.value })} className="w-full px-3 py-2 border border-black/15 rounded-md bg-white" data-testid="transfer-date" aria-invalid={errors.some((x) => x.field === "transfer-date")} /></label>
                </div>
                <label className="flex items-start gap-2 font-semibold cursor-pointer" id="transfer-confirmed">
                  <input type="checkbox" checked={transfer.confirmed} onChange={(e) => setTransfer({ ...transfer, confirmed: e.target.checked })} className="mt-1" data-testid="transfer-confirmed" />
                  <span>I confirm I have made this transfer of {symbol}{Number(amount || 0).toLocaleString()} to the Dutsen Kauna Foundation account above.</span>
                </label>
              </div>
            )}
                        <div className="flex items-start gap-2 p-3 rounded-md bg-[#f8f4e8] border border-[#d6aa43]/20 mb-5 text-sm text-[#0a0d12]/80" data-testid="gateway-notice">
              <Info size={16} className="text-[#d6aa43] mt-0.5 flex-shrink-0" />
              {currency === "NGN" && method === "paystack"
                ? "Pay securely by card, bank app or USSD through Paystack. You are sent to Paystack to complete the payment and return here with your donor reference; your official receipt is emailed once Finance reviews the payment."
                : currency === "NGN"
                  ? "Naira bank transfers: make the transfer to the Foundation's account shown above, then confirm it below. You receive a donor reference immediately and a receipt once Finance matches the credit."
                  : "Card payments in £/US$ are handled securely by Stripe."}
            </div>

            <DonationUse className="mb-4" />
            <div className="flex flex-wrap items-center gap-3 mb-4 text-[11px] text-[#0a0d12]/70" data-testid="secure-badges"><span className="inline-flex items-center gap-1 font-bold"><ShieldCheck size={14} className="text-green-700" /> Secure payment</span><span className="px-2 py-0.5 rounded bg-[#00C3F7]/15 text-[#0a0d12] font-bold">Bank transfer</span><span className="px-2 py-0.5 rounded bg-[#635bff]/15 text-[#0a0d12] font-bold">Stripe</span><span>256-bit encrypted · instant receipt</span>{dash?.donations_this_month > 0 && <span className="ml-auto font-semibold" data-testid="donations-this-month">{dash.donations_this_month} {dash.donations_this_month === 1 ? "person has" : "people have"} donated this month</span>}</div>
            <button type="button" onClick={() => setShowPolicy(!showPolicy)} className="md:hidden text-[11px] underline text-[#0d1724] mb-2" data-testid="policy-toggle">{showPolicy ? "Hide" : "Read"} restricted-gift & tax policy</button>
            <div className={`text-[11px] text-[#0a0d12]/60 leading-relaxed mb-4 space-y-1 ${showPolicy ? "" : "hidden md:block"}`} data-testid="donation-policy">
              <p><strong>Restricted gifts:</strong> donations to a named programme or project are kept for that purpose. Where a project receives more funding than required, any permitted surplus may be redirected to a closely related verified need according to the Foundation's donation policy and the donor's restrictions.</p>
              <p><strong>Tax:</strong> tax treatment depends on the donor's jurisdiction and the legal entity receiving the donation. Donors should obtain appropriate advice where necessary.</p>
            </div>
            <button type="submit" disabled={loading || !bankReady} className="dk-btn-primary w-full justify-center disabled:opacity-60" data-testid="submit-donation" title={!bankReady ? "Confirm your bank transfer first" : undefined}>
              <Heart size={18} /> {loading ? "Submitting..." : bankStep ? "I have transferred — complete my donation" : method === "paystack" ? "Pay by card now" : "Donate now"}
            </button>
            {amount && !loading && <div className="md:hidden fixed left-0 right-0 bottom-0 z-40 p-3 bg-[#155e3f]/95 backdrop-blur border-t border-[#d6aa43]/30" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }} data-testid="sticky-donate-bar"><button type="submit" className="dk-btn-primary w-full justify-center"><Heart size={16} /> Donate {symbol}{Number(amount).toLocaleString()}{donationType !== "one-time" ? ` / ${donationType}` : ""}</button></div>}
          </form>

          <aside className="lg:col-span-2 space-y-5">
            <div className="bg-[#155e3f] text-white rounded-2xl p-7" data-testid="register-free-card">
              <HandHeart className="text-[#d6aa43] mb-3" />
              <h3 className="font-serif text-xl font-bold mb-2">Prefer not to donate?</h3>
              <p className="text-sm text-white mb-4 leading-relaxed">
                Beneficiary registration is completely free. Donating does not improve your chances of selection or give you extra entries.
              </p>
              <Link to="/empowerment/register" className="dk-btn-outline w-full justify-center" data-testid="register-free-without-donating">
                Register free without donating <ArrowRight size={14} />
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-black/5">
              <ShieldCheck className="text-[#d6aa43] mb-3" />
              <h3 className="font-serif text-xl text-[#0a0d12] font-bold mb-2">Trust & accountability</h3>
              <p className="text-sm text-[#0a0d12]/75 leading-relaxed">
                Donations are managed with integrity, documented through project records, and reported through impact updates and financial summaries.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      <input required={required} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={testid} />
    </div>
  );
}
